import React, { useState, useRef } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';

interface UploadPhotosProps {
  domain: string;
}

interface FileProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  previewUrl: string;
}

const CLASSES = Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`);
const SECTIONS = ['A', 'B', 'C', 'D', 'E'];

function generateAcademicYears(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
}

const ACADEMIC_YEARS = generateAcademicYears();

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

const UploadPhotos: React.FC<UploadPhotosProps> = ({ domain }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [section, setSection] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [enrollmentID, setEnrollmentID] = useState('');
  const [files, setFiles] = useState<FileProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files);

    const validFiles: FileProgress[] = [];
    const errors: string[] = [];

    selected.forEach((file) => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Not a valid image (JPG, PNG, GIF, WebP only)`);
      } else if (file.size > MAX_IMAGE_SIZE) {
        errors.push(`${file.name}: Too large (max 10MB)`);
      } else {
        validFiles.push({
          file,
          progress: 0,
          status: 'pending',
          previewUrl: URL.createObjectURL(file),
        });
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    setFiles((prev) => [...prev, ...validFiles]);
    // Reset input so same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (!selectedClass || !section || !academicYear) {
      alert('Please select Class, Section, and Academic Year');
      return;
    }
    if (files.length === 0) {
      alert('Please select at least one photo');
      return;
    }

    setUploading(true);
    setSuccessCount(0);
    let completed = 0;

    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'done') continue;

      // Mark as uploading
      setFiles((prev) =>
        prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading' as const, progress: 0 } : f))
      );

      try {
        const file = files[i].file;
        const timestamp = Date.now();
        const uniqueId = Math.random().toString(36).substring(2, 9);
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${timestamp}_${uniqueId}_${safeName}`;
        const storagePath = `${domain}/photo-gallery/${academicYear}/${selectedClass}/${section}/${fileName}`;

        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setFiles((prev) =>
                prev.map((f, idx) => (idx === i ? { ...f, progress } : f))
              );
            },
            (error) => reject(error),
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Save metadata to Firestore
              await addDoc(collection(db, 'school-users', domain, 'photo-gallery'), {
                imageUrl: downloadURL,
                fileName,
                originalName: file.name,
                className: selectedClass,
                section,
                academicYear,
                enrollmentID: enrollmentID.trim() || '',
                uploadedAt: Timestamp.now(),
                size: file.size,
              });

              setFiles((prev) =>
                prev.map((f, idx) => (idx === i ? { ...f, status: 'done' as const, progress: 100 } : f))
              );
              completed++;
              setSuccessCount(completed);
              resolve();
            }
          );
        });
      } catch (error: any) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'error' as const, error: error.message } : f
          )
        );
      }
    }

    setUploading(false);
  };

  const clearCompleted = () => {
    setFiles((prev) => {
      prev.filter((f) => f.status === 'done').forEach((f) => URL.revokeObjectURL(f.previewUrl));
      return prev.filter((f) => f.status !== 'done');
    });
    setSuccessCount(0);
  };

  return (
    <div>
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h5 className="card-title mb-4">
            <i className="bi bi-cloud-upload me-2 text-primary-custom"></i>Upload Photos
          </h5>

          {/* Class, Section, Year & Enrollment ID */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Class</label>
              <select
                className="form-select"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                disabled={uploading}
              >
                <option value="">Select Class</option>
                {CLASSES.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Section</label>
              <select
                className="form-select"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                disabled={uploading}
              >
                <option value="">Select</option>
                {SECTIONS.map((sec) => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Academic Year</label>
              <select
                className="form-select"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                disabled={uploading}
              >
                <option value="">Select Year</option>
                {ACADEMIC_YEARS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Enrollment ID <small className="text-muted">(optional)</small></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. ENR-2025-001"
                value={enrollmentID}
                onChange={(e) => setEnrollmentID(e.target.value)}
                disabled={uploading}
              />
            </div>
          </div>

          {/* File Picker */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Select Photos</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <div className="form-text">Accepted: JPG, PNG, GIF, WebP. Max 10MB per image.</div>
          </div>

          {/* Preview Grid */}
          {files.length > 0 && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold">{files.length} photo(s) selected</span>
                {successCount > 0 && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={clearCompleted}>
                    Clear Completed
                  </button>
                )}
              </div>
              <div className="row g-3">
                {files.map((fp, idx) => (
                  <div key={idx} className="col-6 col-md-3 col-lg-2">
                    <div className="card border h-100 position-relative">
                      <img
                        src={fp.previewUrl}
                        alt={fp.file.name}
                        className="card-img-top"
                        style={{ height: 120, objectFit: 'cover' }}
                      />
                      {/* Status overlay */}
                      {fp.status === 'done' && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                          style={{ background: 'rgba(40,167,69,0.5)' }}
                        >
                          <i className="bi bi-check-circle-fill text-white fs-2"></i>
                        </div>
                      )}
                      {fp.status === 'error' && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                          style={{ background: 'rgba(220,53,69,0.5)' }}
                        >
                          <i className="bi bi-x-circle-fill text-white fs-2"></i>
                        </div>
                      )}
                      {/* Remove button */}
                      {fp.status === 'pending' && (
                        <button
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                          style={{ width: 24, height: 24, padding: 0, lineHeight: '24px' }}
                          onClick={() => removeFile(idx)}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                      {/* Progress bar */}
                      {fp.status === 'uploading' && (
                        <div className="position-absolute bottom-0 start-0 w-100">
                          <div className="progress" style={{ height: 6, borderRadius: 0 }}>
                            <div
                              className="progress-bar bg-primary-custom"
                              style={{ width: `${fp.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      <div className="card-body p-2">
                        <small className="text-truncate d-block" title={fp.file.name}>
                          {fp.file.name}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn bg-primary-custom text-white"
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>Upload Photos
                </>
              )}
            </button>
            {successCount > 0 && (
              <span className="text-success fw-semibold">
                <i className="bi bi-check-circle me-1"></i>{successCount} photo(s) uploaded successfully
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPhotos;
